/**
 * One discipline activity, in full: its stage gates (engineering, procurement,
 * pre-commissioning) or its quantities (fabrication & construction), the link
 * that ties it to the macro activity, and every attribute the register holds.
 */
import { useEffect, useState } from 'react';
import { authFetch } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Loader2 } from 'lucide-react';
import { StageGateTable } from './DisciplinePlanActual';
import { DISCIPLINE_META, HEALTH_COLORS } from './microLinkRows';

const API = process.env.REACT_APP_BACKEND_URL;

export function MicroActivityDetail({ open, onOpenChange, projectId, modelId, linkId, health }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !linkId || !modelId) return;
    let dead = false;
    setLoading(true); setError(null); setData(null);
    (async () => {
      try {
        const r = await authFetch(`${API}/api/v1/projects/${projectId}/4d/models/${modelId}/micro-links/${linkId}`);
        const d = await r.json();
        if (dead) return;
        if (!r.ok) setError(d.detail || 'Could not open that activity');
        else setData(d);
      } catch (e) { if (!dead) setError(e.message); }
      finally { if (!dead) setLoading(false); }
    })();
    return () => { dead = true; };
  }, [open, linkId, modelId, projectId]);

  const meta = data ? DISCIPLINE_META[data.discipline] : null;
  const q = data?.quantity;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[520px] sm:max-w-[520px] overflow-y-auto"
                    data-testid="micro-activity-detail">
        <SheetHeader>
          <SheetTitle className="text-sm flex items-center gap-2">
            {meta && (
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                    style={{ background: `${meta.color}22`, color: meta.color }}>
                {meta.short}
              </span>
            )}
            <span className="font-mono text-[11px] text-muted-foreground">{data?.activityCode}</span>
            <span className="truncate">{data?.activityName || 'Discipline activity'}</span>
          </SheetTitle>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-6">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading the register item…
          </div>
        )}
        {error && <p className="text-xs text-red-500 py-4" data-testid="micro-detail-error">{error}</p>}

        {data && (
          <div className="space-y-4 py-3 text-xs">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border p-2">
                <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Progress</div>
                <div className="text-sm font-semibold">{data.progressPct}%</div>
              </div>
              <div className="rounded-md border p-2">
                <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Health</div>
                <div className="text-sm font-semibold"
                     style={{ color: HEALTH_COLORS[health] || HEALTH_COLORS[data.status] }}>
                  {(health || data.status || '—').replace(/_/g, ' ')}
                </div>
              </div>
              <div className="rounded-md border p-2">
                <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Link weight</div>
                <div className="text-sm font-semibold">{Number(data.link?.weight || 0).toFixed(1)}%</div>
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                Delivers macro activity
              </p>
              <p className="text-[11px]">
                <span className="font-mono text-muted-foreground mr-1.5">{data.link?.macroActivityCode}</span>
                {data.link?.macroActivityName}
              </p>
              {data.link?.linkedBy && (
                <Badge variant="outline" className="text-[9px] h-4 px-1 mt-1">
                  linked by {data.link.linkedBy}
                  {data.link.confidence != null && ` · ${Math.round(data.link.confidence * 100)}% confidence`}
                </Badge>
              )}
              {data.link?.rationale && (
                <p className="text-[10px] text-muted-foreground mt-1">{data.link.rationale}</p>
              )}
            </div>

            {data.measure === 'QUANTITY' && q && (
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Quantities</p>
                <table className="w-full text-[11px]" data-testid="micro-detail-quantity">
                  <tbody>
                    {[['Scope', `${q.scopeQuantity} ${q.uom}`],
                      ['Done to date', `${q.actualQty} ${q.uom}`],
                      ['Remaining', `${q.remainingQty} ${q.uom}`]].map(([k, v]) => (
                      <tr key={k} className="border-t border-border">
                        <td className="px-1.5 py-1 text-muted-foreground">{k}</td>
                        <td className="px-1.5 py-1 text-right font-medium">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.measure === 'STAGE_GATE' && (
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Stage gates</p>
                <StageGateTable gates={data.gates} />
              </div>
            )}

            {data.attributes?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Details</p>
                <table className="w-full text-[11px]" data-testid="micro-detail-attributes">
                  <tbody>
                    {data.attributes.map(a => (
                      <tr key={a.label} className="border-t border-border">
                        <td className="px-1.5 py-1 text-muted-foreground">{a.label}</td>
                        <td className="px-1.5 py-1 text-right font-medium">{a.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default MicroActivityDetail;
